import { Controller } from '@hotwired/stimulus';
import { Offcanvas } from 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Bootstrap owns the drawer interaction. Finish its lifecycle before Turbo
// replaces or morphs the shell, including refreshes while it is animating.
export default class extends Controller {
  static targets = ['drawer', 'trigger'];

  disconnect() {
    this.cleanup();
  }

  shown() {
    this.triggerTargets.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
  }

  hidden() {
    this.triggerTargets.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  open(event) {
    event.preventDefault();
    const show = () => {
      if (this.element.isConnected) Offcanvas.getOrCreateInstance(this.drawerTarget).show();
    };
    const dialog = event.currentTarget.closest('dialog.utmr');
    // The Menu button also invokes modal#hideModal. Wait for its lifecycle so
    // the existing sidebar is interactive outside the native modal dialog.
    if (dialog) (dialog.closest('turbo-frame') || dialog).addEventListener('modal:closed', show, { once: true });
    else show();
  }

  navigate(event) {
    if (!event.target.closest('a[href]:not([data-bs-toggle])')) return;

    this.restoreFocus = this.triggerTarget.getAttribute('aria-expanded') === 'true';
    this.close();
  }

  async beforeRender(event) {
    if (event.type === 'turbo:before-frame-render' && event.target !== this.element.closest('turbo-frame')) return;

    const restoreFocus = this.restoreFocus || this.triggerTarget.getAttribute('aria-expanded') === 'true';
    this.restoreFocus = false;
    if (restoreFocus) {
      const render = event.detail.render;
      event.detail.render = async (...args) => {
        await render(...args);
        const trigger = Array.from(document.querySelectorAll('[data-navigation-target="trigger"]')).find(
          (element) => element.getClientRects().length
        );
        trigger?.focus({ preventScroll: true });
      };
    }
    event.preventDefault();
    await this.close();
    this.cleanup();
    event.detail.resume();
  }

  close() {
    const drawer = this.drawerTarget;
    const instance = Offcanvas.getInstance(drawer);
    if (!instance || !drawer.matches('.show, .showing, .hiding')) return Promise.resolve();
    if (this.closing) return this.closing;

    // Complete an in-flight opening before asking Bootstrap to hide it.
    if (drawer.classList.contains('showing')) drawer.dispatchEvent(new Event('transitionend'));
    this.closing = new Promise((resolve) => {
      drawer.addEventListener(
        'hidden.bs.offcanvas',
        () => {
          this.closing = null;
          resolve();
        },
        { once: true }
      );
      instance.hide();
    });
    return this.closing;
  }

  cleanup() {
    if (!this.hasDrawerTarget) return;
    const drawer = this.drawerTarget;
    const instance = Offcanvas.getInstance(drawer);
    if (!instance) return;

    // Cache/disconnect cannot wait for CSS transitions. Flush only this drawer's
    // pending callbacks before disposal so Bootstrap restores its scroll lock.
    this.close();
    drawer.parentElement?.querySelector(':scope > .offcanvas-backdrop')?.dispatchEvent(new Event('transitionend'));
    drawer.dispatchEvent(new Event('transitionend'));
    instance.dispose();
  }
}
